import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Message, Poster, Status } from '@bitcine/cinesend-theme/dist'
import useStore from '../../../hooks/use_store'
import AssetIdentifier from '../asset_identifier'
import Date from '../date'
import FieldValue from '../../field_value'
import Languages from './languages'
import { useNavigate, useParams } from 'react-router-dom'
import useAuth from '../../../hooks/use_auth'
import { assetStatus } from '../../../constants/asset_statuses'
import { convertToLongDate } from '../../../helpers/convert_date'
import { CHILD_ENTITY_LABEL, GROUP_ASSET_TYPES } from '../../../constants/group_asset_types'

const LENGTH = 'Length'
const POSTER = 'Poster'
const RELEASE_YEAR = 'Year of Release'
const FEATURE_FILE_NAME = 'Feature File Name (.mp4)'

const StandardFields = ({ asset, cycleID }) => {
  const params = useParams()
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const queryKey = [`field-values`, `?assetID=${asset.id}`, `&cycleID=${cycleID}`, `&isStandard=true`]
  const refreshValues = () => queryClient.invalidateQueries(queryKey)
  const { data: fields, isInitialLoading, isError, error } = useQuery(queryKey)
  const { updateModel } = useStore()
  const saveAsset = (data) =>
    updateModel.mutate(
      {
        endpoint: `cycles/${cycleID}/assets`,
        id: asset.id,
        data,
      },
      {
        onSuccess: (res) => {
          if (res.data.asset.id !== asset.id) {
            navigate(
              `/airlines/${params.airlineID}/cycles/${cycleID}/metadata/${params.assetType}/${res.data.asset.id}/details/${params['*']}`
            )
          }
        },
      }
    )

  const isGroupAsset = GROUP_ASSET_TYPES.includes(asset.asset_type)

  const posterField = fields?.find((field) => field.name === POSTER)
  const posterUrl = posterField?.asset_image?.url
  const lengthField = fields?.find((field) => field.name === LENGTH)
  const releaseYearField = fields?.find((field) => field.name === RELEASE_YEAR)
  const featureFileName = fields?.find((field) => field.name === FEATURE_FILE_NAME)
  const assetDateIssue = asset?.asset_issues?.find((issue) => issue.validator_group === 'date_order')

  const scrollToPoster = () => {
    const poster = document.getElementById('field-poster')
    poster.scrollIntoView({ behavior: 'smooth' })
  }
  const { user } = useAuth()

  return (
    <Status pending={isInitialLoading} isError={isError} errorMessage={error?.message}>
      <div className='flex space-x-4'>
        {posterField ? (
          <div className='w-full max-w-60 cursor-pointer' onClick={scrollToPoster}>
            {posterField.value !== null && posterField.asset_image !== null ? (
              <div className='size-full'>
                <Poster url={posterUrl} />
              </div>
            ) : (
              <div className='size-full'>
                <div className='flex justify-center items-center p-14 relative aspect-[2/3] bg-gray-100'>Poster</div>
              </div>
            )}
          </div>
        ) : null}

        <div className='w-full space-y-4'>
          <div className='flex space-x-4'>
            <AssetIdentifier asset={asset} saveAsset={saveAsset} />
          </div>
          <div className='w-full flex space-x-4'>
            {lengthField ? (
              <FieldValue
                cacheKey={queryKey}
                refreshValues={() => refreshValues()}
                field={lengthField}
                assetID={asset.id}
              />
            ) : null}
            {releaseYearField ? (
              <FieldValue
                cacheKey={queryKey}
                refreshValues={() => refreshValues()}
                cycleID={cycleID}
                field={releaseYearField}
                assetID={asset.id}
              />
            ) : null}

            {asset.asset_type !== 'ad' &&
              (isGroupAsset ? (
                <div className='shrink-0'>
                  <div className='flex space-x-6'>
                    {[
                      ['Start Date', asset.start_date],
                      ['End Date', asset.end_date],
                    ].map(([label, date]) => (
                      <div key={label}>
                        <p className='text-xs text-gray-500 mb-1'>{label}</p>
                        <p className='text-sm'>{convertToLongDate(date)}</p>
                      </div>
                    ))}
                  </div>
                  <p className='text-xs text-gray-400 mt-1'>Derived from {CHILD_ENTITY_LABEL[asset.asset_type]}.</p>
                </div>
              ) : (
                <>
                  <Date
                    asset={asset}
                    type={'Start'}
                    saveAsset={saveAsset}
                    disabled={
                      [assetStatus.Holdover, assetStatus.Expiring].includes(asset.status) &&
                      !user.has_unrestricted_edit_role
                    }
                  />
                  <Date asset={asset} type={'End'} saveAsset={saveAsset} />
                </>
              ))}
          </div>
          {assetDateIssue && (
            <div className='w-full'>
              <Message error={true} small={true}>
                {assetDateIssue.description}
              </Message>
            </div>
          )}
          <Languages queryKey={queryKey} cycleID={cycleID} asset={asset} fields={fields} />
          {featureFileName ? (
            <FieldValue
              cacheKey={queryKey}
              cycleID={cycleID}
              field={featureFileName}
              assetID={asset.id}
              refreshValues={refreshValues}
            />
          ) : null}
        </div>
      </div>
    </Status>
  )
}

export default StandardFields
